import { useInfiniteQuery } from '@tanstack/react-query'
import { FaArrowLeft, FaRegNewspaper } from 'react-icons/fa'
import { Link } from 'react-router-dom'

import api from 'services/api'
import * as S from './style'

type RadarNewsItem = {
  id: string
  category?: string | null
  title: string
  slug: string
  excerpt?: string | null
  coverImage?: string | null
  publishedAt?: string | null
  readingMinutes?: number | null
}

type RadarNewsPage = {
  items: RadarNewsItem[]
  total: number
  page: number
  limit: number
  hasMore: boolean
}

const FALLBACK = '/images/itinerary-banner.png'

const formatDate = (value?: string | null) => {
  if (!value) return ''

  return new Date(value).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  })
}

const RadarNews = () => {
  const {
    data,
    isLoading,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    refetch
  } = useInfiniteQuery({
    queryKey: ['radar-news'],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const response = await api.get<RadarNewsPage>('/radar/noticias', {
        params: { page: pageParam, limit: 12 }
      })
      return response.data
    },
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.page + 1 : undefined
  })

  const items = data?.pages.flatMap((page) => page.items) ?? []
  const total = data?.pages[0]?.total ?? 0

  return (
    <S.Page>
      <S.Hero>
        <S.HeroInner>
          <S.BackLink to="/radar">
            <FaArrowLeft /> Voltar ao Radar
          </S.BackLink>
          <S.Eyebrow>
            <FaRegNewspaper /> Radar RedSterna
          </S.Eyebrow>
          <h1>Notícias e dicas de viagem</h1>
          <p>
            Conteúdos para informar, inspirar e ajudar você a planejar melhor
            cada viagem.
          </p>
          {!isLoading && !isError && (
            <S.Count>
              {total} {total === 1 ? 'publicação' : 'publicações'}
            </S.Count>
          )}
        </S.HeroInner>
      </S.Hero>

      <S.Container>
        {isLoading ? (
          <S.State>Carregando notícias...</S.State>
        ) : isError ? (
          <S.State>
            Não foi possível carregar as notícias agora.
            <button type="button" onClick={() => refetch()}>
              Tentar novamente
            </button>
          </S.State>
        ) : items.length ? (
          <>
            <S.Grid>
              {items.map((item) => (
                <S.Card key={item.id}>
                  <Link to={'/radar/' + item.slug}>
                    <S.Image
                      src={item.coverImage || FALLBACK}
                      alt={item.title}
                    />
                  </Link>
                  <S.CardBody>
                    {item.category && <S.Badge>{item.category}</S.Badge>}
                    <h2>
                      <Link to={'/radar/' + item.slug}>{item.title}</Link>
                    </h2>
                    {item.excerpt && <p>{item.excerpt}</p>}
                    <S.Meta>
                      {formatDate(item.publishedAt)}
                      {item.readingMinutes
                        ? ' · ' + item.readingMinutes + ' min de leitura'
                        : ''}
                    </S.Meta>
                  </S.CardBody>
                </S.Card>
              ))}
            </S.Grid>

            {hasNextPage && (
              <S.LoadMore
                type="button"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? 'Carregando...' : 'Carregar mais'}
              </S.LoadMore>
            )}
          </>
        ) : (
          <S.State>Nenhuma notícia publicada ainda.</S.State>
        )}
      </S.Container>
    </S.Page>
  )
}

export default RadarNews
